import React from 'react'
import { submissionService } from '@oneblink/apps-react'
import { FormTypes } from '@oneblink/types'

import AutoSaveControlledForm from '../../../components/AutoSaveControlledForm'

import { useAppConfig } from '../../../providers/AppConfigProvider'
import useSubmitForm from '../../../hooks/useSubmitForm'
import { getCouncilContactInfo } from '../../../lib/councilContactInfo'

import { toast } from 'react-toastify'

type Props = { form: FormTypes.Form; preFillData?: Record<string, unknown> }

const UNINCORPORATED_FAR_WEST_COUNCIL_ID = '00000000000000A1'
const SYDNEY_HARBOUR_FEDERATION_TRUST_COUNCIL_ID = '0000000000000103'

function Form({ form, preFillData }: Props) {
  const { buttons } = useAppConfig()
  const [isPreparingToSubmit, setIsPreparingToSubmit] =
    React.useState<boolean>(false)

  const determinePagesShowing = React.useCallback(
    (submission: Record<string, unknown>) =>
      submission.IssueWith ===
        'Retail, e.g. cafe, restaurant, takeaway, grocery store, food stall' &&
      submission.LabelCompOrUseByIssue === 'No' &&
      submission.FirstOccuredInRetail === 'Yes' &&
      (submission.PeopleIllLiveInTheSameHousehold === '1' ||
        ['0', '1'].includes(submission.FoodPoisoning as string)) &&
      ['Yes', 'No'].includes(submission.AnyoneSufferedInjury as string) &&
      submission.AllergicReaction === 'No' &&
      !(
        submission.Local_Govt_Area_Id === UNINCORPORATED_FAR_WEST_COUNCIL_ID ||
        submission.Local_Govt_Area_Id ===
          SYDNEY_HARBOUR_FEDERATION_TRUST_COUNCIL_ID
      ),
    [],
  )

  const [hidePagesState, setHidePagesState] = React.useState<boolean>(
    determinePagesShowing(preFillData || {}),
  )

  /* update definition here */
  const updateDefinition = React.useCallback(
    (submission: Record<string, unknown>, definition: FormTypes.Form) => {
      const hidePages = determinePagesShowing(submission)
      setHidePagesState(hidePages)
      // need to use all the conditions involved in displaying AllergicReaction too
      if (hidePages) {
        return { ...definition, elements: definition.elements.slice(0, 2) }
      }

      return definition
    },
    [determinePagesShowing],
  )

  /* update submission data here */
  const updateSubmission = React.useCallback(
    (newSubmission: Record<string, unknown>) => {
      return newSubmission
    },
    [],
  )

  const customButtons = React.useMemo(() => {
    return {
      ...buttons,
      submit: {
        ...buttons?.submit,
        label: hidePagesState ? 'Show Details' : buttons?.submit?.label,
      },
    }
  }, [hidePagesState, buttons])

  const [
    { onSubmit, clearServerValidationMessage },
    { serverValidationMessage, isSubmitting },
  ] = useSubmitForm({
    formDefinition: form,
    preFillData,
    skipServerValidation: false,
  })

  const complaintSubmit = React.useCallback(
    async (formSubmission: submissionService.NewFormSubmission) => {
      setIsPreparingToSubmit(true)

      let submissionWithAdditionalValues: Record<string, unknown> = {
        ...formSubmission.submission,
      }

      const councilId = submissionWithAdditionalValues.Local_Govt_Area_Id as
        | string
        | undefined

      let councilContactInfo
      const queryObj: Record<string, string> = {}
      try {
        if (
          councilId &&
          councilId !== 'unsure' &&
          councilId !== UNINCORPORATED_FAR_WEST_COUNCIL_ID &&
          councilId !== SYDNEY_HARBOUR_FEDERATION_TRUST_COUNCIL_ID
        ) {
          councilContactInfo = await getCouncilContactInfo(councilId)

          if (councilContactInfo) {
            submissionWithAdditionalValues = {
              ...formSubmission.submission,
              contactName: councilContactInfo.contactName,
              councilId: councilContactInfo.councilId,
              councilName: councilContactInfo.councilName,
              councilEmail: councilContactInfo.email,
              councilFax: councilContactInfo.fax,
              councilPhoneNumber: councilContactInfo.phoneNumber,
            }
          }
        }

        if (councilContactInfo) {
          queryObj.councilName = councilContactInfo.councilName
          queryObj.councilEmail = councilContactInfo.email
          queryObj.councilPhoneNumber = councilContactInfo.phoneNumber
          queryObj.councilFax = councilContactInfo.fax
        } else if (councilId === 'unsure') {
          queryObj.councilName = councilId
        }
      } catch (e) {
        // setSubmission(formSubmission.submission)
        toast.error('There was an error getting council details', {
          position: 'bottom-right',
          hideProgressBar: true,
        })
        setIsPreparingToSubmit(false)
        return
      }

      try {
        return await onSubmit(
          { ...formSubmission, submission: submissionWithAdditionalValues },
          { queryStringArgs: queryObj },
        )
      } catch (e) {
        setIsPreparingToSubmit(false)
      }
    },
    [onSubmit],
  )

  return (
    <AutoSaveControlledForm
      form={form}
      initialSubmission={preFillData}
      onSubmit={complaintSubmit}
      buttons={customButtons}
      updateSubmission={updateSubmission}
      updateDefinition={updateDefinition}
      isSubmitting={isPreparingToSubmit || isSubmitting}
      serverValidationMessage={serverValidationMessage}
      clearServerValidationMessage={clearServerValidationMessage}
    />
  )
}

export default Form
